"use client";

import { useQueryClient } from "@tanstack/react-query";
import { type UseBuyBoxOptions, useBuyBoxFactory, useSelectionKey } from "@trydig/centra/client";
import { useDPC } from "@/lib/depict/dpc";
import { type GtmItem, trackAddToCart } from "@/lib/gtm";
import { useModal } from "@/lib/hooks/useModal";

type UseBuyBoxProps = Omit<UseBuyBoxOptions, "onSuccess"> & {
	/** Depict product_id of the item being added. Used to fire `sendAddToCartEvent`. */
	depictProductId?: string;
	/** GA4 item for the `add_to_cart` event, fired on a successful add. */
	gtmItem?: GtmItem;
	/** Currency code for the GA4 event (e.g. "NOK"). */
	gtmCurrency?: string;
	/** Fires after the shared post-add behaviour (opens cart, invalidates, DPC + GTM events). */
	onSuccess?: () => void;
};

export const useBuyBox = ({
	depictProductId,
	gtmItem,
	gtmCurrency,
	onSuccess,
	...props
}: UseBuyBoxProps) => {
	const { open } = useModal("cart");
	const queryClient = useQueryClient();
	const selectionKey = useSelectionKey();
	const { data: dpc } = useDPC();

	return useBuyBoxFactory({
		...props,
		onSuccess: () => {
			open();
			queryClient.invalidateQueries({ queryKey: selectionKey });
			if (dpc && depictProductId) {
				dpc.sendAddToCartEvent({ product_id: depictProductId });
			}
			if (gtmItem) {
				trackAddToCart({
					currency: gtmCurrency,
					value: gtmItem.price != null ? gtmItem.price * (gtmItem.quantity ?? 1) : undefined,
					items: [gtmItem],
				});
			}
			onSuccess?.();
		},
	});
};

export type UseBuyBoxReturn = ReturnType<typeof useBuyBox>;
